import React from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

const shiurScrollLoop = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
`;

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`;

const Title = styled.h2`
  color: #2d3653;
  text-align: center;
  font-family: "Style Script", cursive;
  font-size: clamp(16px, 1.5vw, 24px);
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 0;
  }
`;

const ScrollContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  position: relative;
  min-height: 120px;

  @media (max-width: 768px) {
    min-height: 100px;
  }
`;

const ScrollContent = styled.div`
  animation: ${({ $autoScroll }) =>
    $autoScroll ? css`${shiurScrollLoop} 40s linear infinite` : "none"};

  &:hover {
    animation-play-state: paused;
  }
`;

const CourseTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 3px 0;
  border-bottom: 1px solid rgba(45, 54, 83, 0.2);

  &:last-child {
    border-bottom: none;
  }
`;

const DaysRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #2d3653;
  font-family: "IBM Plex Sans JP", sans-serif;
  font-size: ${(props) =>
    props.fontSize ? `clamp(${props.fontSize}px, 1vw, ${props.fontSize}px)` : "clamp(14px, 1vw, 18px)"};
  font-weight: 500;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TimeLabel = styled.div`
  color: #2d3653;
  font-family: "IBM Plex Sans JP", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${(props) =>
    props.fontSize ? `clamp(${props.fontSize}px, 1vw, ${props.fontSize}px)` : "clamp(14px, 1vw, 18px)"};
`;

const TimeValue = styled.div`
  color: #2d3653;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${(props) =>
    props.fontSize ? `clamp(${props.fontSize}px, 1vw, ${props.fontSize}px)` : "clamp(14px, 1vw, 18px)"};
`;

const WeeklyCourseContainer = ({
  shiur,
  displayDay = true,
  displayRav = false,
  fontSize,
  autoScroll = false,
}) => {
  const { t } = useTranslation();

  const formatShiurTime = (time, endTime) => {
    const formattedTime = time.substring(0, 5);
    const formattedEndTime =
      endTime && endTime !== "00:00:00" ? ` / ${endTime.substring(0, 5)}` : "";
    return `${formattedTime}${formattedEndTime}`;
  };

  const formatDayName = (day) => {
    return t(`shiur.daysOfWeek.${day}`);
  };

  const groupCoursesByDay = (courses) => {
    if (!courses || typeof courses !== "object") return [];

    const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "sunday"];
    const groupedDays = [];

    dayOrder.forEach((day) => {
      if (courses[day] && courses[day].length > 0) {
        groupedDays.push({
          day,
          courses: courses[day],
        });
      }
    });

    return groupedDays;
  };

  const groupedCourses = groupCoursesByDay(shiur);

  const renderDayBlocks = (keyPrefix) =>
    groupedCourses.map((dayGroup, dayIndex) => (
      <div key={`${keyPrefix}day-${dayGroup.day}`}>
        <DaysRow
          fontSize={fontSize}
          style={{
            fontWeight: "bold",
            marginTop: dayIndex > 0 ? "5px" : "0",
            marginBottom: "8px",
            borderBottom: "2px solid rgba(45, 54, 83, 0.3)",
            paddingBottom: "4px",
          }}
        >
          {formatDayName(dayGroup.day)}
        </DaysRow>

        {dayGroup.courses.map((course, courseIndex) => (
          <CourseTable key={`${keyPrefix}${dayGroup.day}-${courseIndex}`}>
            <InfoRow>
              <TimeLabel fontSize={fontSize}>
                {course.subject}
                {displayRav && ` - ${course.ravName}`}
                {course.roomName && ` - ${course.roomName}`}
              </TimeLabel>
              <TimeValue fontSize={fontSize}>
                {formatShiurTime(course.time, course.endTime)}
              </TimeValue>
            </InfoRow>
          </CourseTable>
        ))}
      </div>
    ));

  if (!groupedCourses.length) {
    return null;
  }

  return (
    <PanelRoot>
      <Title>{t("display.weeklyCourse")}</Title>
      <ScrollContainer>
        <ScrollContent $autoScroll={autoScroll}>
          {renderDayBlocks("")}
          {autoScroll ? renderDayBlocks("dup-") : null}
        </ScrollContent>
      </ScrollContainer>
    </PanelRoot>
  );
};

export default WeeklyCourseContainer;
